import { IVariableRule, IQueryVariables, ExpressionOperator } from "./variables/variable-models";
import { JsReportConfiguration } from "@main/pdf-exporting/js-report/js-report.models";
import { DatabaseType } from "@main/dbdoc";
import { SelectItem } from "primeng/api";

export type EditionFunc<T> = (item: T) => Promise<T>;

export type WidgetSourceType = "control-set" | "table" | "chart" | "html";

export type WidgetOutputMode = "web" | "PDF";

export function getWidgetSourceTypeLabel(type: WidgetSourceType): string {
    switch (type) {
        case "control-set":
            return "Control Set";
        case "table":
            return "Table";
        case "chart":
            return "Chart";
        case "html":
            return "HTML";
    }
}

export type DataType = "bool" | "date" | "numeric" | "other" | "string";

export function getDataTypeEnumAsOptions(): SelectItem[] {
    return <SelectItem[]>[
        { label: "Bool", value: "bool" },
        { label: "Date", value: "date" },
        { label: "Numeric", value: "numeric" },
        { label: "Other", value: "other" },
        { label: "String", value: "string" }
    ];
}

export type InputType = "calendar" | "checkbox" | "dropdown" | "multiselect" | "number" | "text" | "textarea";

export interface IVariable {
    id: number;
    name: string;
}

export interface IVariableInfo {
    name: string;
}

export interface IWidgetSource {
    id: string;
    name?: string;
    code?: string;
    title?: string;
    createdOn: Date;
    isDraft: boolean;
    level: WidgetAccessLevel;
    widgetType: WidgetSourceType;

    // Foreign keys and navigational properties.
    ownerId?: string;
    ownerName?: string;
    organizationIds: number[];
    releaseWidgetId?: string;
    displayRuleId?: number;
    displayRule?: IVariableRule;
}

export interface IWidgetSourcePreload {
    id: string;
    widgetType: WidgetSourceType;
}

export enum WidgetAccessLevel {
    Local = 0,
    Public = 1
}

export interface IQuerySource {
    id: string;
    isDraft: boolean;
    queryType: string;
    name: string;
    createdOn: Date;
    level: QueryAccessLevel;
    filterMode?: QueryFilterMode;

    // Foreign keys and navigational properties.
    ownerId?: string;
    ownerName?: string;
    organizationIds: number[];
    releaseQueryId?: string;
}

export interface IQuerySourcePreload {
    id: string;
    name: string;
    queryType: string;
}

export interface ICustomColumnType {
    id: string;
    name: string;
    mask: string;
}

export interface IQuerySchema {
    columns: IQuerySchemaColumn[];
}

export interface IQuerySchemaColumn {
    isKey: boolean;
    dataType: DataType;
    queryAlias: string;
    columnName: string;
    baseColumnName: string;
}

export interface IQueryColumnAggregation {
    queryAlias: string;
    expressions: string[];
}

export interface IQueryAggregationsRequest {
    queryVariables?: IQueryVariables;
    aggregations: IQueryColumnAggregation[];
}

export interface IViewMetadata {
    customColumnTypes: ICustomColumnType[];
    columns: IViewMetadataColumn[];
}

export interface IViewMetadataColumn {
    mask?: string;
    minWidth?: number;
    maxWidth?: number;
    queryAlias: string;
    title?: string;
}

export interface ITableSet {
    id: string;
    folderId: string;
    folderSourceCode: string;
}

export interface ITableSetFolder {
    id: string;
    name: string;
    sourceCode: string;
    databaseType?: DatabaseType;
}

export interface ITableSetTable {
    id: string;
    name: string;
    folderId: string;
    tableAlias: string;
    sourceCode: string;
    parentTableId?: string;
    columns: ITableSetColumn[];
    children: ITableSetTable[];
}

export interface ITableSetColumn {
    id: string;
    name: string;
    tableId: string;
    columnAlias: string;
    isPrimaryKey: boolean;
    isForeignKey: boolean;
    table: ITableSetTable;
}

export interface ITableSetFolderInfo {
    id: string;
    sourceCode: string;
    tables: ITableSetTable[];
    databaseType?: DatabaseType;
}

export interface IPagedGridSettings {
    skip?: number;
    take?: number;
    sortingField?: string;
    sortingDirection?: number;
}

export interface IQueryDataRequest {
    tableId: string;
    folderId: string;
    sourceCode: string;
    valueColumnId: string;
    labelColumnId: string;
    filterOperand?: string;
    parentTableId?: string;
    filterOperator?: ExpressionOperator;
    filterColumnId?: string;
    queryVariables?: IQueryVariables;
    widgetSourceId?: string;
}

export interface IQueryPageRequest {
    gridSettings?: IPagedGridSettings;
    queryVariables?: IQueryVariables;
}

export enum PdfExportSource {
    Dashboard = "Reporting Dashboard",
    WidgetGrid = "Reporting Widget Grid",
    WidgetHtml = "Reporting Widget HTML",
    WidgetChart = "Reporting Widget Chart",
    WidgetControlSet = "Reporting Widget Control-Set"
}

export interface PdfExportSettings {
    configuration: JsReportConfiguration;
    queryPageRequest?: IQueryPageRequest;
    source?: PdfExportSource;
    allowedWidgets?: string[];
    fileName?: string;
}

export enum QueryAccessLevel {
    Local = 0,
    Public = 1,
    Restricted = 2
}

export enum QueryFilterMode {
    UserOrganizationFilter = 0,
    UserOrganizationsFilter = 1,
    NoFilter = 2
}

export interface IOwnerUser {
    id: string;
    userId: number;
    userName: string;
}